<template>
  <div
    class="market-info-table bg--color-01 sha--04 bor--radius-12 pt-20--l pb-20--l pl-24--l pr-24--l"
  >
    <client-only>
      <infinite-scroll :disabled="!showLoadMore" @intersect="showMore">
        <exc-table
          :heading="{
            coin: $t('marketInfo.table.currency'),
            price: $t('marketInfo.table.price'),
            change: $t('marketInfo.table.change'),
            volume: $t('marketInfo.table.volume'),
            market: $t('marketInfo.table.market'),
            action: '',
          }"
          :rows="tableRows"
          :loading="loading"
          bg-in-head
          :empty-message="$t('marketInfo.table.emptyMessage')"
          adaptive
          hide-header-border
          class="market-info-table__table"
        >
          <template #td-coin="{ coin }">
            <div class="flx">
              <img
                class="market-info-table__logo-image"
                :src="coin.img"
                :alt="coin.label"
                @error="setDefaultImage"
              >
              <div class="market-info-table__logo-inner flx flx--col ml-8">
                <div
                  class="txt--bold txt--color-04 txt--14px txt--height-18px mb-2"
                  v-text="coin.name"
                />
                <div
                  class="txt--weight-500 txt--color-03 txt--12px txt--height-14px"
                  v-text="coin.label"
                />
              </div>
            </div>
          </template>
          <template #td-price="{ price }">
            <span class="txt--weight-500 txt--14px txt--height-18px">
              {{ price }}
            </span>
          </template>
          <template #td-change="{ change }">
            <span
              class="txt--weight-500 txt--14px txt--height-18px"
              :class="{
                'txt--red': change.startsWith('-'),
                'txt--green': change.startsWith('+'),
              }"
              v-text="change"
            />
          </template>
          <template #td-volume="{ volume }">
            <span class="txt--weight-500 txt--14px txt--height-18px">
              {{ `${volume ? '$' : ''} ${volume}` }}
            </span>
          </template>
          <template #td-market="{ market }">
            <span class="txt--weight-500 txt--14px txt--height-18px">
              {{ `${market ? '$' : ''} ${market}` }}
            </span>
          </template>
          <template #td-action="{ action }">
            <exc-dropdown
              class="market-info-table__dropdown"
              :options="action"
              :placeholder="$t('marketInfo.table.trade')"
              size="s"
            >
              <template #selected>
                <span
                  class="txt--color-03"
                  v-text="$t('marketInfo.table.trade')"
                />
              </template>

              <template #item="{ option }">
                <div
                  class="market-info-table__dropdown-item txt--left"
                  @click="localePush(`/exchange/${option.name}`)"
                  v-text="option.name"
                />
              </template>
            </exc-dropdown>
          </template>
        </exc-table>
      </infinite-scroll>
    </client-only>
  </div>
</template>

<script>
import { ExcTable, ExcDropdown } from '@/components/common'
import Decimal from 'decimal.js-light'
import { mapGetters } from 'vuex'
import { formatShortNumber } from '~/lib/utils'

const VOLUME_CALC_TIMEOUT = 30 * 1000 // 30 sec
const MARKET_VOLUME = new Map()
const MARKET_ROW = new Map()

export default {
  name: 'MarketInfoTable',

  components: {
    ExcTable,
    ExcDropdown,
  },

  props: {
    columns: {
      type: Array,
      default: () => [],
    },
    rows: {
      type: Array,
      default: () => [],
    },
    showLoadMore: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      addMoreLoading: false,
      tableKey: Date.now(),
    }
  },

  computed: {
    ...mapGetters({
      marketCap: 'welcome/getMarketCap',
      marketPairs: 'pairs/getCoinGroups',
      pairsList: 'pairs/pairsList',
    }),

    tableRows () {
      if (this.loading) return []

      return this.createTableRows(this.rows)
    },
  },

  methods: {
    showMore () {
      this.$emit('show-more')
    },

    checkIsUSDT ({ moneyTicker }) {
      return moneyTicker === 'USDT'
    },

    convertNum (num) {
      if (isNaN(parseFloat(num))) return 0

      return this.$filters.formatNumber(this.$filters.shortifyNumber(num))
    },

    getUSDTPrice ({ volume, moneyTicker }) {
      let price = new Decimal(0)

      if (!this.checkMoneyValue(volume)) return price

      const currentTicker = this.marketPairs[moneyTicker]?.find(
        this.checkIsUSDT,
      )

      if (this.checkMoneyValue(currentTicker?.price)) {
        const volumeDecimal = new Decimal(volume)
        const priceDecimal = new Decimal(currentTicker.price)

        price = volumeDecimal.mul(priceDecimal)
      }

      return price
    },

    checkMoneyValue (str) {
      if (!str) return false

      const num = parseFloat(`${str}`)

      return !isNaN(num) && !!num
    },

    createActions ({ name }) {
      return {
        name,
        pairName: name.split(name.replace(/[^-_/]/g, '')).join('_'),
      }
    },

    createTableRows (pairs) {
      return this.createMarketTableRows(pairs)
    },

    formatDate (date) {
      if (date && date.length > 0) {
        return date.split(' ')[0]
      }

      return date
    },

    createMarketTableRows (pairs) {
      return pairs
        .map(([key, value]) => {
          const currentDate = Date.now()
          let fromMemo

          if (MARKET_ROW.has(key)) {
            const { value, date } = MARKET_ROW.get(key)

            if (currentDate - date < VOLUME_CALC_TIMEOUT) {
              fromMemo = value
            }
          }

          const USDTData = value.find(this.checkIsUSDT)

          if (!USDTData) return undefined

          const change = this.formatChange(USDTData.change)
          const price = key === 'USDT' ? '1' : this.convertNum(USDTData.price)

          if (fromMemo) {
            return {
              ...fromMemo,
              price,
              change,
            }
          }

          const market = this.marketCap[key]?.cup
            ? formatShortNumber(this.marketCap[key].cup)
            : ''
          const volume = this._memoVolumeResolver(key, value)

          const result = {
            id: key,
            coin: {
              name: key,
              label: USDTData.stockLabel,
              img:
                USDTData.stockTickerImg ||
                require('@/static/img/balance/coinDefaultIcon.svg'),
            },
            price,
            change,
            volume,
            market,
            action: value.filter(Boolean).map(this.createActions),
          }

          MARKET_ROW.set(key, {
            value: result,
            date: currentDate,
          })

          return result
        })
        .filter(Boolean)
    },

    formatChange (change) {
      const val = parseFloat(change)

      if (isNaN(val) || !val) return '0%'

      return `${val > 0 ? '+' : ''}${val}%`
    },

    calcTotalVolumeInUSDT (pairsGroup) {
      return pairsGroup
        .reduce(
          (total, current) =>
            total.plus(
              this.checkIsUSDT(current)
                ? new Decimal(current.volumeInMoney)
                : this.getUSDTPrice(current),
            ),
          new Decimal(0),
        )
        .toString()
    },

    _memoVolumeResolver (ticker, pairsGroup) {
      const date = Date.now()
      const key = JSON.stringify(pairsGroup.map(({ id }) => id)) + ticker

      if (MARKET_VOLUME.has(key)) {
        const fromMemo = MARKET_VOLUME.get(key)

        if (date - fromMemo.date < VOLUME_CALC_TIMEOUT) {
          return fromMemo.result
        } else {
          MARKET_VOLUME.delete(key)
        }
      }

      const volume =
        this.marketCap[ticker]?.totalVolume ||
        this.calcTotalVolumeInUSDT(pairsGroup)
      const result = volume ? formatShortNumber(volume) : ''

      MARKET_VOLUME.set(key, { date, result })

      return result
    },
    setDefaultImage (e) {
      e.target.src = require('@/static/img/balance/coinDefaultIcon.svg')
    },
  },
}
</script>

<style lang="scss" scoped>
$table-class: '.exc-table';
$table-bp: 1050px;

.market-info-table {
  display: flex;
  flex-direction: column;
  width: 100%;

  &::v-deep {
    #{$table-class}__heading-container {
      font-size: toRem(14px);
    }

    #{$table-class}__head {
      padding-top: toRem(20px);
      padding-bottom: toRem(20px);
    }

    @include bp(0, $table-bp) {
      padding: 0;

      #{$table-class}__cell-coin {
        justify-content: flex-start;
        background: rgba(145, 148, 159, 0.04);

        &::after {
          display: none;
        }
      }

      td {
        min-height: auto;
        padding-top: toRem(8px);
        padding-bottom: toRem(8px);

        &::after {
          font-size: toRem(14px);
        }
      }

      tr {
        padding-top: toRem(16px);
      }

      tr:not(:last-child):not(.exc-table__no-items-row) {
        border: none;
      }
    }
  }

  &__logo-image {
    width: toRem(34px);
    height: toRem(34px);

    @include bp(0, $table-bp) {
      text-align: left;
    }
  }

  &__logo-inner {
    @include bp(0, $table-bp) {
      align-items: flex-start;
    }
  }

  &__dropdown {
    &::v-deep {
      position: relative;

      @include bp(0, $table-bp) {
        width: 100%;
        z-index: 2;
      }

      .exc-input-label-error-wrapper__content {
        margin-bottom: 0;
      }
    }
  }

  &__dropdown-item {
    padding: toRem(10px);
  }
}
</style>
